// src/pages/Dashboard.js
// Dashboard con datos reales del Sheets BD_MAESTRA_SIG + HubSpot pipeline + Finanzas
import React, { useState, useEffect } from 'react';
import { leerProyectosSheets, leerEntregablesSheets, leerDealsHubSpot, leerFinanzasSheets } from '../services/claudeApi';

// ── HubSpot etapas ──────────────────────────────────────────────────────────
const HUBSPOT_STAGES = {
  appointmentscheduled:   { label: 'Reunión agendada',   color: '#94A3B8', prob: 10 },
  qualifiedtobuy:         { label: 'Calificado',         color: '#F59E0B', prob: 20 },
  presentationscheduled:  { label: 'Propuesta enviada',  color: '#3B82F6', prob: 50 },
  decisionmakerboughtin:  { label: 'Decisor convencido', color: '#8B5CF6', prob: 80 },
  contractsent:           { label: 'Contrato enviado',   color: '#F97316', prob: 90 },
  closedwon:              { label: 'Cerrado ganado',     color: '#22C55E', prob: 100 },
  closedlost:             { label: 'Cerrado perdido',    color: '#EF4444', prob: 0  },
};

const getStage = (stage) =>
  HUBSPOT_STAGES[stage] || { label: stage, color: '#94A3B8', prob: 10 };

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt$ = (n) => n >= 1000 ? `$${(n/1000).toFixed(0)}K` : `$${n}`;
const fmtFull$ = (n) => `$${Number(n || 0).toLocaleString('es-EC', { minimumFractionDigits: 0 })}`;

function StatCard({ label, value, sub, color, icon }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{icon} {label}</div>
      <div className="stat-value" style={{ color: color || '#1A2E44', fontSize: 26 }}>{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

function ProgressBar({ pct, color = '#E8863A' }) {
  return (
    <div style={{ background: '#F0F4F8', borderRadius: 4, height: 6, width: '100%', overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.5s' }} />
    </div>
  );
}

function SemTag({ estado }) {
  const cfg = {
    'ACTIVO':       { bg: '#DCFCE7', color: '#15803D', label: 'Activo' },
    'EN_REVISION':  { bg: '#FEF3C7', color: '#B45309', label: 'En revisión' },
    'ENTREGADO':    { bg: '#DBEAFE', color: '#1D4ED8', label: 'Entregado' },
    'CANCELADO':    { bg: '#FEE2E2', color: '#DC2626', label: 'Cancelado' },
  }[estado] || { bg: '#F0F4F8', color: '#6B7A89', label: estado };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
      {cfg.label}
    </span>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function Dashboard({ setActivePage, setProyectoActivo }) {
  const [proyectos,    setProyectos]    = useState([]);
  const [finanzas,     setFinanzas]     = useState([]);
  const [entregables,  setEntregables]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [lastUpdate,   setLastUpdate]   = useState('');
  const [hubspotDeals, setHubspotDeals] = useState([]);

  useEffect(() => {
    const cargar = async () => {
      setLoading(true);
      try {
        const [proy, ent, deals, fin] = await Promise.all([
          leerProyectosSheets(),
          leerEntregablesSheets('').catch(() => []),
          leerDealsHubSpot().catch(() => []),
          leerFinanzasSheets().catch(() => []),
        ]);
        setProyectos(proy || []);
        setEntregables(ent || []);
        setHubspotDeals(deals || []);
        setFinanzas(fin || []);
        setLastUpdate(new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }));
      } catch (e) {
        setError('No se pudieron cargar los datos del Sheets. ' + e.message);
      }
      setLoading(false);
    };
    cargar();
  }, []);

  // ── KPIs calculados ────────────────────────────────────────────────────────
  const activos    = proyectos.filter(p => p.estado === 'ACTIVO').length;
  const enRevision = proyectos.filter(p => p.estado === 'EN_REVISION').length;
  const entregados = proyectos.filter(p => p.estado === 'ENTREGADO').length;

  const totalFacturado = finanzas.reduce((s, f) => s + (f.facturado || 0), 0);
  const totalCobrado   = finanzas.reduce((s, f) => s + (f.cobrado   || 0), 0);
  const totalSaldo     = finanzas.reduce((s, f) => s + (f.saldo     || 0), 0);

  const pipelineTotal     = hubspotDeals.reduce((s, d) => s + (d.monto || 0), 0);
  const pipelinePonderado = hubspotDeals.reduce((s, d) => s + (d.monto || 0) * (getStage(d.etapa).prob / 100), 0);

  const atrasados = entregables.filter(e => e.estado === 'Pendiente').length;

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 12 }}>
      <div style={{ width: 32, height: 32, border: '3px solid #E8863A', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      <span style={{ color: '#6B7A89', fontSize: 14 }}>Cargando datos del sistema...</span>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            Sistema Integrado ARCSEG v2.0 · {new Date().toLocaleDateString('es-EC', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            {lastUpdate && <span style={{ marginLeft: 8, fontSize: 11, color: '#94A3B8' }}>· Actualizado {lastUpdate}</span>}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setActivePage('nuevo')}>+ Nuevo Proyecto</button>
      </div>

      {error && (
        <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#92400E' }}>
          ⚠️ {error}
        </div>
      )}

      {/* KPIs principales */}
      <div className="stats-grid">
        <StatCard icon="📋" label="Proyectos Activos"      value={activos}            sub={`+${enRevision} en revisión`}          color="#1A2E44" />
        <StatCard icon="📊" label="Pipeline HubSpot"       value={hubspotDeals.length} sub={`${fmt$(pipelineTotal)} en juego`}      color="#B45309" />
        <StatCard icon="✅" label="Entregados"             value={entregados}          sub="Este ciclo"                             color="#15803D" />
        <StatCard icon="💰" label="Facturado Total"        value={fmt$(totalFacturado)} sub={`${fmt$(totalCobrado)} cobrado`}        color="#1D4ED8" />
        <StatCard icon="⚠️" label="Por Cobrar"            value={fmt$(totalSaldo)}    sub={atrasados > 0 ? `${atrasados} entregables pendientes` : 'Al día'} color={totalSaldo > 0 ? '#DC2626' : '#15803D'} />
      </div>

      {/* Barras de progreso KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div className="card">
          <div className="card-title">Proyectos en Plazo</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 28, fontWeight: 600, color: '#1A2E44' }}>
              {proyectos.length > 0 ? Math.round((activos / Math.max(proyectos.length, 1)) * 100) : 0}%
            </span>
            <span style={{ fontSize: 13, color: '#6B7A89' }}>meta ≥ 80%</span>
          </div>
          <ProgressBar pct={proyectos.length > 0 ? (activos / Math.max(proyectos.length, 1)) * 100 : 0} />
        </div>
        <div className="card">
          <div className="card-title">Pipeline Ponderado HubSpot</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 28, fontWeight: 600, color: '#1A2E44' }}>{fmt$(pipelinePonderado)}</span>
            <span style={{ fontSize: 13, color: '#6B7A89' }}>de {fmt$(pipelineTotal)} total</span>
          </div>
          <ProgressBar pct={pipelineTotal > 0 ? (pipelinePonderado / pipelineTotal) * 100 : 0} color="#3B82F6" />
        </div>
      </div>

      {/* Proyectos activos + Pipeline HubSpot */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 16, marginBottom: 24 }}>

        {/* Tabla proyectos */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, color: '#1A2E44', margin: 0 }}>📋 Proyectos Activos</h2>
            <button className="btn btn-ghost btn-sm" onClick={() => setActivePage('tracker')}>Ver todos →</button>
          </div>
          {proyectos.length === 0 ? (
            <p style={{ color: '#94A3B8', fontSize: 13, textAlign: 'center', padding: 20 }}>No hay proyectos registrados</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #E8ECF0' }}>
                    {['PROYECTO', 'CLIENTE', 'AVANCE', 'ESTADO'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: '#6B7A89', fontWeight: 600, fontSize: 11 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {proyectos.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #F4F6F8', cursor: 'pointer' }}
                        onClick={() => { setProyectoActivo && setProyectoActivo(p); setActivePage('tracker'); }}>
                      <td style={{ padding: '8px', fontWeight: 500, color: '#1A2E44' }}>
                        <div>{p.nombre || p.codigo}</div>
                        <div style={{ fontSize: 11, color: '#94A3B8' }}>{p.codigo}</div>
                      </td>
                      <td style={{ padding: '8px', color: '#6B7A89' }}>{p.cliente}</td>
                      <td style={{ padding: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <ProgressBar pct={parseFloat(p.avance) * 100 || 0} />
                          <span style={{ fontSize: 11, color: '#6B7A89', minWidth: 28 }}>
                            {Math.round((parseFloat(p.avance) || 0) * 100)}%
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '8px' }}><SemTag estado={p.estado} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pipeline HubSpot */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, color: '#1A2E44', margin: 0 }}>🎯 Pipeline Comercial</h2>
            <a href="https://app.hubspot.com/contacts/51177381/deals" target="_blank" rel="noopener noreferrer"
               style={{ fontSize: 12, color: '#E8863A', textDecoration: 'none', fontWeight: 500 }}>
              Ver en HubSpot →
            </a>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {hubspotDeals.map((deal, i) => {
              const stage = getStage(deal.etapa);
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#F8FAFC', borderRadius: 8, border: '1px solid #E8ECF0' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: stage.color, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#1A2E44', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {deal.nombre}
                    </div>
                    <div style={{ fontSize: 11, color: '#94A3B8' }}>{stage.label}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    {deal.monto > 0 && (
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#1A2E44' }}>{fmtFull$(deal.monto)}</div>
                    )}
                    <div style={{ fontSize: 11, color: stage.color, fontWeight: 600 }}>{stage.prob}%</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 12, padding: '8px 10px', background: '#F0F4F8', borderRadius: 8, display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: '#6B7A89' }}>Pipeline total</span>
            <span style={{ fontWeight: 600, color: '#1A2E44' }}>{fmtFull$(pipelineTotal)}</span>
          </div>
        </div>
      </div>

      {/* Estado Financiero por Proyecto — lee de FINANZAS_PROYECTO */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: '#1A2E44', margin: 0 }}>💰 Estado Financiero por Proyecto</h2>
        </div>
        {proyectos.length === 0 ? (
          <p style={{ color: '#94A3B8', fontSize: 13, textAlign: 'center', padding: 20 }}>Sin datos financieros disponibles</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E8ECF0' }}>
                  {['PROYECTO', 'CLIENTE', 'CONTRATO', 'FACTURADO', 'COBRADO', 'SALDO', 'DÍAS REST.'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: '#6B7A89', fontWeight: 600, fontSize: 11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {proyectos.map((p, i) => {
                  const dias = parseInt(p.diasRestantes) || 0;
                  const fin  = finanzas.find(f => f.codigo === p.codigo) || {};
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #F4F6F8' }}>
                      <td style={{ padding: '10px', fontWeight: 500, color: '#1A2E44' }}>
                        <div>{p.nombre || p.codigo}</div>
                        <div style={{ fontSize: 11, color: '#94A3B8' }}>{p.codigo}</div>
                      </td>
                      <td style={{ padding: '10px', color: '#6B7A89' }}>{p.cliente}</td>
                      <td style={{ padding: '10px', fontWeight: 600, color: '#1A2E44' }}>{fmtFull$(fin.monto || 0)}</td>
                      <td style={{ padding: '10px', fontWeight: 600, color: '#2E75B6' }}>{fmtFull$(fin.facturado || 0)}</td>
                      <td style={{ padding: '10px', fontWeight: 600, color: '#15803D' }}>{fmtFull$(fin.cobrado || 0)}</td>
                      <td style={{ padding: '10px', fontWeight: 600, color: (fin.saldo || 0) > 0 ? '#DC2626' : '#15803D' }}>{fmtFull$(fin.saldo || 0)}</td>
                      <td style={{ padding: '10px' }}>
                        <span style={{ fontWeight: 600, color: dias < 0 ? '#DC2626' : dias < 30 ? '#F59E0B' : '#15803D' }}>
                          {dias < 0 ? `${Math.abs(dias)}d atrasado` : `${dias}d`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Fila de totales */}
              <tfoot>
                <tr style={{ borderTop: '2px solid #E8ECF0', background: '#F8FAFC' }}>
                  <td colSpan={2} style={{ padding: '10px', fontWeight: 700, color: '#1A2E44', fontSize: 12 }}>TOTALES</td>
                  <td style={{ padding: '10px', fontWeight: 700, color: '#1A2E44' }}>{fmtFull$(finanzas.reduce((s,f) => s+(f.monto||0),0))}</td>
                  <td style={{ padding: '10px', fontWeight: 700, color: '#2E75B6' }}>{fmtFull$(totalFacturado)}</td>
                  <td style={{ padding: '10px', fontWeight: 700, color: '#15803D' }}>{fmtFull$(totalCobrado)}</td>
                  <td style={{ padding: '10px', fontWeight: 700, color: '#DC2626' }}>{fmtFull$(totalSaldo)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}